import type { AssignmentCriterion, CriteriaTemplateItem, GradeLevel, PaperType } from "@markeroo/shared";
import { ui } from "@/strings";
import { getPaperTypeLabel } from "@/strings/labels";
import PiVerificationCheckSolid from "@markeroo/icons/src/solid/general/PiVerificationCheckSolid";
import { Tooltip } from "@/components/Tooltip";
import { Pill, usePillAnimating } from "@/components/Pill";

interface CriteriaPillProps {
  criterion: AssignmentCriterion | CriteriaTemplateItem;
  onEdit: () => void;
  onDelete: () => void;
  isDeleting?: boolean;
  isNew?: boolean;
  paperType?: PaperType | null;
  gradeLevel?: GradeLevel | null;
}

/**
 * Icon with tooltip that disables during pill animation.
 * Must be rendered inside a Pill to access the animation context.
 */
function CriterionIcon({ tooltip }: { tooltip: string }) {
  const isPillAnimating = usePillAnimating();

  return (
    <Tooltip content={tooltip} disabled={isPillAnimating}>
      <PiVerificationCheckSolid
        size={22}
        className="text-brand-700 flex-shrink-0"
        aria-label={tooltip}
      />
    </Tooltip>
  );
}

/**
 * CriteriaPill component - Displays a criterion as a pill/chip
 *
 * Uses the standard Pill component with:
 * - Default variant for standard criteria (subtle, with check-badge icon)
 * - Emphasis variant for custom criteria (stands out more, no icon)
 */
export function CriteriaPill({
  criterion,
  onEdit,
  onDelete,
  isDeleting = false,
  isNew = false,
  paperType,
  gradeLevel,
}: CriteriaPillProps) {
  // Check if this is a standard criterion (not custom)
  const isStandardCriterion = !criterion.key.startsWith("CUSTOM_");

  // Build tooltip text for standard criteria
  const standardCriterionTooltip = (() => {
    if (!isStandardCriterion) return "";
    const paperTypeLabel = getPaperTypeLabel(paperType ?? null, gradeLevel);
    return `This is a standard ELA-appropriate focus area for a ${paperTypeLabel} paper type for a grade ${gradeLevel ?? "N/A"} class`;
  })();

  // Icon for standard criteria (22x22px, brand-700 color per design spec)
  const criterionIcon = isStandardCriterion ? (
    <CriterionIcon tooltip={standardCriterionTooltip} />
  ) : undefined;

  const displayLabel = criterion.label || (
    <span className="italic text-theme-muted">
      {ui.labels.untitledCriterion}
    </span>
  );

  return (
    <Pill
      variant={isStandardCriterion ? "default" : "emphasis"}
      icon={criterionIcon}
      onEdit={onEdit}
      onDelete={onDelete}
      isDeleting={isDeleting}
      isNew={isNew}
      ariaLabel={`Edit ${criterion.label || ui.labels.untitledCriterion}`}
    >
      {displayLabel}
    </Pill>
  );
}
